import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField, MenuItem, Button as MuiButton } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import useModal from "../../hooks/useModal";
import Button from "../../components/ui/Button";
import { grievanceSchema } from "../../schemas/grievanceSchema";
import { raiseGrievance } from "../../services/grievance";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const RequiredLabel = ({ text }) => (
  <>
    {text} <span style={{ color: "#d32f2f" }}>*</span>
  </>
);

const departmentOptions = ["HR", "Admin", "Support", "Account", "IT"];
const priorityOptions = ["Low", "Medium", "High"];
const confidentialityOptions = [
  { value: "Normal", label: "Normal — visible to HR" },
  {
    value: "Confidential",
    label: "Confidential — visible to HR Manager only (notified by email)",
  },
];

const AddGrievance = ({ refresh, categories = [], getSubcategories }) => {
  const { hideModal } = useModal();

  const [subcategories, setSubcategories] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: zodResolver(grievanceSchema),
    defaultValues: {
      department: "HR",
      category_id: "",
      subcategory_id: "",
      subject: "",
      description: "",
      incident_date: null,
      priority: "Medium",
      confidentiality_level: "Normal",
      preferred_resolution: "",
    },
  });

  const selectedCategoryId = watch("category_id");

  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  useEffect(() => {
    if (!selectedCategoryId) {
      setSubcategories([]);
      return;
    }
    getSubcategories?.(selectedCategoryId)
      .then((list) => setSubcategories(list || []))
      .catch((error) => {
        console.log("Error loading subcategories:", error);
        toast.error(
          error?.response?.data?.message || "Failed to load sub categories"
        );
      });
  }, [selectedCategoryId, getSubcategories]);

  // Same validation shape as Timesheet's handleFileUpload (extension
  // whitelist + size cap + max file count), same 4MB limit.
  const ALLOWED_FILE_TYPES = [
    ".doc",
    ".docx",
    ".pdf",
    ".xls",
    ".xlsx",
    ".jpg",
    ".jpeg",
    ".png",
  ];
  const MAX_FILE_SIZE_MB = 4;
  const MAX_FILES = 5;

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const totalFilesCount = uploadedFiles.length + files.length;

    if (totalFilesCount > MAX_FILES) {
      toast.error(`You can upload a maximum of ${MAX_FILES} files.`);
      e.target.value = "";
      return;
    }

    const validFiles = [];
    const invalidTypeFiles = [];
    const oversizedFiles = [];

    files.forEach((file) => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(ext)) {
        invalidTypeFiles.push(file.name);
      } else if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    setUploadedFiles((prev) => [...prev, ...validFiles]);

    let message = "";
    if (invalidTypeFiles.length)
      message += `${invalidTypeFiles.length} file(s) rejected — unsupported type.\n`;
    if (oversizedFiles.length)
      message += `${oversizedFiles.length} file(s) rejected — over ${MAX_FILE_SIZE_MB}MB.\n`;
    if (message) toast.error(message);

    e.target.value = "";
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (key === "incident_date") {
          formData.append(
            "incident_date",
            value ? value.toISOString().split("T")[0] : ""
          );
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      uploadedFiles.forEach((file) => formData.append("documents", file));

      const res = await raiseGrievance(formData);

      if (res.status === 200 || res.status === 201) {
        toast.success("Grievance raised successfully");
        refresh?.();
        hideModal();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to save grievance"
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-3">
        <Controller
          name="department"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label={<RequiredLabel text="Department" />}
              fullWidth
              InputLabelProps={{ shrink: true, sx: { fontSize: "1rem" } }}
              error={!!errors.department}
              helperText={errors.department?.message}
            >
              {departmentOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="category_id"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label={<RequiredLabel text="Category" />}
              fullWidth
              error={!!errors.category_id}
              helperText={errors.category_id?.message}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="subcategory_id"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Sub Category"
              fullWidth
              disabled={!selectedCategoryId}
            >
              {subcategories.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="subject"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={<RequiredLabel text="Subject" />}
              fullWidth
              error={!!errors.subject}
              helperText={errors.subject?.message}
            />
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Description"
              fullWidth
              multiline
              rows={3}
            />
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="incident_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={<RequiredLabel text="Incident Date" />}
              value={field.value}
              onChange={field.onChange}
              minDate={threeMonthsAgo}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.incident_date,
                  helperText: errors.incident_date?.message,
                },
              }}
            />
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <TextField {...field} select label="Priority" fullWidth>
              {priorityOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="confidentiality_level"
          control={control}
          render={({ field }) => (
            <TextField {...field} select label="Confidentiality" fullWidth>
              {confidentialityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </div>

      <div className="mb-3">
        <Controller
          name="preferred_resolution"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Preferred Resolution"
              fullWidth
              multiline
              rows={2}
            />
          )}
        />
      </div>

      <div className="mb-3">
        <MuiButton
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
        >
          Attach Document
          <VisuallyHiddenInput
            type="file"
            multiple
            onChange={handleFileUpload}
            accept=".doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png"
          />
        </MuiButton>
        <div style={{ fontSize: "11.5px", color: "#666", marginTop: "4px" }}>
          Supported: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX — max {MAX_FILE_SIZE_MB}MB each, up to {MAX_FILES} files
        </div>

        {uploadedFiles.length > 0 && (
          <ul style={{ marginTop: "8px", paddingLeft: "18px" }}>
            {uploadedFiles.map((file, index) => (
              <li key={`${file.name}-${index}`}>
                {file.name}{" "}
                <span
                  onClick={() => removeUploadedFile(index)}
                  style={{ color: "red", cursor: "pointer" }}
                >
                  (remove)
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="d-flex justify-content-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={() => hideModal()}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Submit Grievance"}
        </Button>
      </div>
    </form>
  );
};

export default AddGrievance;
